import { ChevronDown, LogOut, Search } from 'lucide-react'
import { useAuthStore } from '@insightai/auth'
import { Button, Popover, ThemeToggle, cn, focusRing } from '@insightai/ui'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceScope } from '../../shared/useRBAC'
import { logout } from '../session'

/** F0-64 — workspace switcher. */
function WorkspaceSwitcher() {
  const { workspace, workspaces } = useWorkspaceScope()
  const setActiveWorkspace = useAuthStore((state) => state.setActiveWorkspace)
  const navigate = useNavigate()

  if (workspaces.length === 0) return null

  return (
    <Popover
      label="Switch workspace"
      trigger={
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 rounded-md border border-border-subtle px-3 py-1.5',
            'text-body-sm text-text-primary hover:bg-surface-hover',
            focusRing,
          )}
        >
          <span className="truncate">{workspace?.name ?? 'Select workspace'}</span>
          <ChevronDown aria-hidden className="size-3.5 text-text-secondary" />
        </button>
      }
    >
      <ul className="flex flex-col gap-0.5">
        {workspaces.map((option) => (
          <li key={option.id}>
            <button
              type="button"
              onClick={() => {
                // §7.2: the query cache is keyed workspace-first, so navigating
                // to the new workspace prefix is what isolates the data. The
                // cache for the old workspace is dropped by the route change.
                setActiveWorkspace(option.id)
                navigate(`/w/${option.id}`)
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-2 text-start text-body-sm',
                option.id === workspace?.id
                  ? 'bg-surface-selected text-text-primary'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                focusRing,
              )}
            >
              <span className="truncate">{option.name}</span>
              {option.id === workspace?.id && (
                <span className="text-caption text-text-tertiary">Current</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </Popover>
  )
}

export function TopBar() {
  const user = useAuthStore((state) => state.user)

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border-subtle bg-surface-raised px-4">
      <WorkspaceSwitcher />

      <button
        type="button"
        className={cn(
          'ms-2 hidden items-center gap-2 rounded-md border border-border-subtle bg-surface-inset px-3 py-1.5 md:flex',
          'text-body-sm text-text-tertiary hover:border-border-strong',
          focusRing,
        )}
      >
        <Search aria-hidden className="size-3.5" />
        <span>Search studies, briefs, findings…</span>
      </button>

      <div className="ms-auto flex items-center gap-3">
        <ThemeToggle />

        {user && (
          <Popover
            label="Account"
            align="end"
            trigger={
              <button
                type="button"
                className={cn(
                  'flex items-center gap-2 rounded-full border border-border-subtle p-0.5 pe-2',
                  focusRing,
                )}
              >
                <span
                  aria-hidden
                  className="flex size-7 items-center justify-center rounded-full bg-brand-solid text-caption font-semibold text-text-on-brand"
                >
                  {(user.full_name ?? user.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden text-body-sm text-text-primary sm:inline">
                  {user.full_name ?? user.email}
                </span>
              </button>
            }
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <p className="text-body-sm font-medium text-text-primary">{user.full_name}</p>
                <p className="text-caption text-text-secondary">{user.email}</p>
                <p className="pt-1 text-caption text-text-tertiary">
                  {/* Roles are shown because "why can't I see X?" is the most
                      common support question in an RBAC product. */}
                  {user.roles.map((role) => role.replace(/_/g, ' ').toLowerCase()).join(', ')}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void logout().then(() => window.location.assign('/'))
                }}
                iconStart={<LogOut aria-hidden className="size-3.5" />}
              >
                Sign out
              </Button>
            </div>
          </Popover>
        )}
      </div>
    </header>
  )
}
